/**
 * Conversation-tray helpers — ports of mobile/src/features/workspace/tray-logic.ts
 * and mobile/src/lib/fuzzy.ts so both apps group and search chats the same way.
 */
import { ThreadSummary } from '../types/threads';

export interface ThreadGroup {
  label: 'Today' | 'Yesterday' | 'This week' | 'Earlier';
  threads: ThreadSummary[];
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** 0 today, 1 yesterday, 2 within the last week, 3 earlier — by local calendar day. */
function bucketOf(iso: string, now: Date): 0 | 1 | 2 | 3 {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const at = new Date(iso).getTime();
  if (at >= startOfToday) return 0;
  if (at >= startOfToday - DAY_MS) return 1;
  if (at >= startOfToday - 6 * DAY_MS) return 2;
  return 3;
}

/** Conversations bucketed by last activity (local days), newest first; empty buckets dropped. */
export function groupThreads(threads: readonly ThreadSummary[], now: Date): ThreadGroup[] {
  const buckets: ThreadGroup[] = [
    { label: 'Today', threads: [] },
    { label: 'Yesterday', threads: [] },
    { label: 'This week', threads: [] },
    { label: 'Earlier', threads: [] },
  ];
  const sorted = [...threads].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  for (const thread of sorted) buckets[bucketOf(thread.updated_at, now)].threads.push(thread);
  return buckets.filter((b) => b.threads.length > 0);
}

/**
 * Subsequence fuzzy score (higher is better, -1 = no match). Consecutive and
 * word-start hits are rewarded so "wk" ranks "Work" above "Weekly backlog".
 */
export function fuzzyScore(query: string, target: string): number {
  const q = query.trim().toLowerCase();
  const t = target.toLowerCase();
  if (q.length === 0) return 0;
  if (q.length > t.length) return -1;
  let score = 0;
  let qi = 0;
  let prev = -1;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] !== q[qi]) continue;
    let bonus = 1;
    if (prev === ti - 1) bonus += 3;
    if (ti === 0 || t[ti - 1] === ' ' || t[ti - 1] === '-') bonus += 2;
    score += bonus;
    prev = ti;
    qi++;
  }
  return qi === q.length ? score : -1;
}

/** Filter + rank items by a fuzzy query against a string accessor. */
export function fuzzyFilter<T>(items: T[], query: string, key: (item: T) => string): T[] {
  if (!query.trim()) return items;
  return items
    .map((item) => ({ item, score: fuzzyScore(query, key(item)) }))
    .filter((r) => r.score >= 0)
    .sort((a, b) => b.score - a.score)
    .map((r) => r.item);
}
